import { Plus, Trash2 } from "lucide-react";
import { Field, ImageUpload, SwitchRow, IconUpload } from "@/components/editor/fields";
import { SOCIAL_FIELDS } from "@/lib/cardUtils";

export const IdentityPanel = ({ card, update }) => {
  const i = card.identity;
  const f = (k) => (v) => update(`identity.${k}`, v);
  return (
    <div className="space-y-4" data-testid="editor-identity-section">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nome" value={i.first_name} onChange={f("first_name")} testId="identity-first-name" />
        <Field label="Cognome" value={i.last_name} onChange={f("last_name")} testId="identity-last-name" />
      </div>
      <Field label="Ruolo professionale" value={i.role} onChange={f("role")} testId="identity-role" />
      <Field label="Azienda o studio" value={i.company} onChange={f("company")} testId="identity-company" />
      <Field label="Breve biografia" area value={i.bio} onChange={f("bio")} testId="identity-bio" hint={`${(i.bio || "").length}/600`} />
      <div className="grid grid-cols-2 gap-3">
        <ImageUpload label="Foto profilo" value={i.avatar_url} onChange={f("avatar_url")} testId="identity-avatar-upload" />
        <ImageUpload label="Logo aziendale" value={i.logo_url} onChange={f("logo_url")} testId="identity-logo-upload" />
      </div>
      <ImageUpload label="Immagine di copertina (opzionale)" aspect="wide" value={i.cover_url} onChange={f("cover_url")} testId="identity-cover-upload" />
    </div>
  );
};

export const ContactsPanel = ({ card, update }) => {
  const c = card.contacts;
  const f = (k) => (v) => update(`contacts.${k}`, v);
  const extra = c.extra || [];
  return (
    <div className="space-y-4" data-testid="editor-contacts-section">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Cellulare" value={c.mobile} onChange={f("mobile")} testId="contacts-mobile" placeholder="+39 333 123 4567" />
        <Field label="Telefono fisso" value={c.phone} onChange={f("phone")} testId="contacts-phone" />
      </div>
      <Field label="WhatsApp (numero con prefisso)" value={c.whatsapp} onChange={f("whatsapp")} testId="contacts-whatsapp" placeholder="+39 333 123 4567" />
      <Field label="Messaggio WhatsApp precompilato" value={c.whatsapp_message} onChange={f("whatsapp_message")} testId="contacts-whatsapp-message" />
      <Field label="Email" type="email" value={c.email} onChange={f("email")} testId="contacts-email" />
      <Field label="Sito web" value={c.website} onChange={f("website")} testId="contacts-website" placeholder="https://" />
      <Field label="Indirizzo" value={c.address} onChange={f("address")} testId="contacts-address" />
      <SwitchRow label="Pulsante Google Maps" checked={c.show_maps} onChange={f("show_maps")} testId="contacts-show-maps" />
      <div className="rounded-lg border bg-white p-3" data-testid="quick-icons-editor">
        <div className="text-xs text-zinc-600">Icone dei pulsanti rapidi</div>
        <p className="mb-2.5 text-[11px] text-zinc-400">Lascia vuoto per l'icona standard, oppure carica la tua (PNG trasparente consigliato).</p>
        <div className="flex flex-wrap gap-4">
          {[["call", "Chiama"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["website", "Sito"], ["maps", "Mappa"]].map(([k, l]) => (
            <div key={k} className="flex flex-col items-center gap-1">
              <IconUpload value={card.icons?.[k]} onChange={(v) => update(`icons.${k}`, v)} testId={`icon-upload-${k}`} />
              <span className="text-[10px] text-zinc-500">{l}</span>
            </div>
          ))}
        </div>
      </div>
      <Field label="Link Google Maps personalizzato (opzionale)" value={c.maps_url} onChange={f("maps_url")} testId="contacts-maps-url" hint="Se vuoto, viene generato dall'indirizzo." />
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-zinc-600">Altri contatti</span>
          <button type="button" data-testid="contacts-add-extra" onClick={() => update("contacts.extra", [...extra, { label: "", value: "", url: "" }])} className="flex items-center gap-1 text-xs text-terra"><Plus className="h-3.5 w-3.5" />Aggiungi</button>
        </div>
        <div className="space-y-2">
          {extra.map((e, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_1.4fr_auto] gap-2 rounded-lg border bg-white p-2">
              <input className="rounded border px-2 py-1.5 text-sm" placeholder="Etichetta (es. PEC)" value={e.label} onChange={(ev) => update(`contacts.extra.${idx}.label`, ev.target.value)} data-testid={`contacts-extra-label-${idx}`} />
              <input className="rounded border px-2 py-1.5 text-sm" placeholder="Valore" value={e.value} onChange={(ev) => update(`contacts.extra.${idx}.value`, ev.target.value)} data-testid={`contacts-extra-value-${idx}`} />
              <button type="button" onClick={() => update("contacts.extra", extra.filter((_, j) => j !== idx))} className="px-1 text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              <input className="col-span-3 rounded border px-2 py-1.5 text-xs" placeholder="Link opzionale (https://, mailto:, tel:)" value={e.url} onChange={(ev) => update(`contacts.extra.${idx}.url`, ev.target.value)} />
            </div>
          ))}
        </div>
      </div>
      <Field label="Note nel file contatto (.vcf)" area rows={2} value={c.vcard_note} onChange={f("vcard_note")} testId="contacts-vcard-note" hint="Incluse solo nel file vCard, non mostrate sulla pagina." />
    </div>
  );
};

export const SocialsPanel = ({ card, update }) => (
  <div className="space-y-3" data-testid="editor-social-section">
    {SOCIAL_FIELDS.map((s) => (
      <div key={s.key} className="flex items-end gap-2">
        <Field className="flex-1" label={s.label} value={card.socials[s.key]} placeholder={s.placeholder} onChange={(v) => update(`socials.${s.key}`, v)} testId={`social-${s.key}`} />
        <IconUpload value={card.icons?.[s.key]} onChange={(v) => update(`icons.${s.key}`, v)} testId={`icon-upload-${s.key}`} title={`Icona personalizzata ${s.label}`} />
      </div>
    ))}
    <p className="text-[11px] text-zinc-400">Il quadratino accanto a ogni social permette di sostituire l'icona standard con una tua immagine.</p>
  </div>
);
